<template>
    <section class="lr-studio">
        <div class="lr-studio__toolbar">
            <div class="lr-studio__toolbar-title">
                <i class="pi pi-sitemap" aria-hidden="true" />
                Rename map
            </div>
            <div class="lr-studio__stats">
                <span class="lr-pill">{{ renameLabels.length }} labels</span>
                <span v-if="changedCount > 0" class="lr-pill lr-pill--warn">
                    {{ changedCount }} pending
                </span>
                <span v-else class="lr-pill lr-pill--accent">In sync</span>
            </div>
        </div>

        <div v-if="loading" class="lr-state">
            <ProgressSpinner style="width: 42px; height: 42px" strokeWidth="4" />
            <p class="lr-state__text">Loading label preferences…</p>
        </div>

        <div v-else-if="renameLabels.length === 0" class="lr-state">
            <i class="pi pi-inbox" aria-hidden="true" />
            <p class="lr-state__title">No labels to rename</p>
            <p class="lr-state__text">
                There are no renamable labels for this company, or the feature is not enabled.
            </p>
        </div>

        <template v-else>
            <div class="lr-map__head" aria-hidden="true">
                <span>System label</span>
                <span></span>
                <span>Shown as</span>
                <span></span>
            </div>

            <div class="lr-map">
                <div
                    v-for="(item, index) in renameLabels"
                    :key="item.default_name ?? item.code ?? index"
                    class="lr-item"
                    :class="{ 'lr-item--dirty': isChanged(item) }"
                >
                    <div class="lr-item__system">
                        <span class="lr-item__name" :title="item.default_name">
                            {{ item.default_name }}
                        </span>
                        <span class="lr-item__meta">
                            Currently
                            <strong>{{ item.current_value || item.default_name }}</strong>
                        </span>
                    </div>

                    <div class="lr-item__bridge" aria-hidden="true">
                        <span class="lr-bridge">
                            <i class="pi pi-arrow-right" />
                        </span>
                    </div>

                    <div class="lr-item__control">
                        <Dropdown
                            class="lr-dropdown"
                            :class="{ 'lr-dropdown--dirty': isChanged(item) }"
                            panelClass="lr-dropdown-panel"
                            v-model="item.selected_value"
                            :options="item.new_names"
                            placeholder="Choose display name"
                            :filter="(item.new_names?.length || 0) > 6"
                            filterPlaceholder="Search labels…"
                            :showClear="false"
                        >
                            <template #value="slotProps">
                                <div v-if="slotProps.value" class="lr-dropdown__value">
                                    <i class="pi pi-tag" aria-hidden="true" />
                                    <span>{{ slotProps.value }}</span>
                                </div>
                                <span v-else class="lr-dropdown__placeholder">
                                    {{ slotProps.placeholder }}
                                </span>
                            </template>
                            <template #option="slotProps">
                                <div class="lr-dropdown__option">
                                    <i class="pi pi-tag" aria-hidden="true" />
                                    <span>{{ slotProps.option }}</span>
                                    <i
                                        v-if="slotProps.option === item.selected_value"
                                        class="pi pi-check lr-dropdown__check"
                                        aria-hidden="true"
                                    />
                                </div>
                            </template>
                        </Dropdown>
                    </div>

                    <div class="lr-item__actions">
                        <Button
                            class="lr-item__reset p-button-rounded p-button-text"
                            icon="pi pi-replay"
                            :disabled="!isChanged(item)"
                            v-tooltip.top="'Reset'"
                            @click="resetRow(item)"
                        />
                    </div>
                </div>
            </div>

            <div v-if="previewItems.length" class="lr-preview">
                <span class="lr-preview__label">Preview</span>
                <span
                    v-for="item in previewItems"
                    :key="`preview-${item.default_name}`"
                    class="lr-preview__chip"
                    :class="{ 'lr-preview__chip--dirty': isChanged(item) }"
                >
                    <i class="pi pi-tag" aria-hidden="true" />
                    {{ item.default_name }}
                    <em>→</em>
                    <strong>{{ item.selected_value }}</strong>
                </span>
            </div>

            <footer class="lr-bar">
                <span class="lr-bar__hint">
                    <template v-if="changedCount > 0">
                        {{ changedCount }} rename{{ changedCount === 1 ? '' : 's' }} ready —
                        the app reloads after save.
                    </template>
                    <template v-else>
                        Adjust a label above to enable save.
                    </template>
                </span>
                <div class="lr-bar__actions">
                    <Button
                        label="Discard"
                        class="lr-btn lr-btn--ghost"
                        icon="pi pi-times"
                        :disabled="!hasChanges || saving"
                        @click="discardChanges"
                    />
                    <Button
                        label="Save renames"
                        class="lr-btn lr-btn--primary"
                        icon="pi pi-check"
                        :disabled="!hasChanges || saving"
                        :loading="saving"
                        @click="saveChanges"
                    />
                </div>
            </footer>
        </template>
    </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { labelRenameStore } from '../../../store/global-stores';

const { renameLabels, loading } = storeToRefs(labelRenameStore);
const saving = ref(false);

const originalValue = (item) => item.original_value ?? item.current_value ?? item.default_name;
const isChanged = (item) => item.selected_value !== originalValue(item);
const changedCount = computed(() => renameLabels.value.filter(isChanged).length);
const hasChanges = computed(() => changedCount.value > 0);

const previewItems = computed(() => {
    const dirty = renameLabels.value.filter(isChanged);
    if (dirty.length > 0) return dirty.slice(0, 6);
    return renameLabels.value.slice(0, 4);
});

const resetRow = (item) => {
    item.selected_value = originalValue(item);
};

const discardChanges = () => {
    renameLabels.value.forEach(resetRow);
};

const saveChanges = async () => {
    if (!hasChanges.value || saving.value) return;
    saving.value = true;
    try {
        await labelRenameStore.saveRenameLabels();
    } finally {
        saving.value = false;
    }
};
</script>
